import { filter, chunk } from 'lodash';
import { Icon } from '@iconify/react';
import { sentenceCase } from 'change-case';
import { useState, useEffect, View } from 'react';
import plusFill from '@iconify/icons-eva/plus-fill';
import backFill from '@iconify/icons-eva/arrow-ios-back-fill';
import { Link as RouterLink } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Modal,
  Box,
  Backdrop,
  Paper
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';

// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { ListHead, ListToolbar, MoreMenu } from '../../components/product';
import PartnerProductModal from './PartnerProductModal';
import ConfDialog from '../../components/ConfDialog';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'code', label: '商品コード', alignRight: false },
  { id: 'category', label: 'カテゴリー', alignRight: false },
  { id: 'name', label: '商品名', alignRight: false },
  { id: 'price', label: '価格', alignRight: true },
  { id: 'commission', label: '契約', alignRight: false },
  // { id: 'size', label: 'サイズ', alignRight: true },
  { id: 'throughput', label: '製造日数', alignRight: true },
  { id: 'expiring', label: ['消費期限', <br />, '(日数)'], alignRight: true },
  // { id: 'caution_count', label: '補充検討', alignRight: true },
  // { id: 'warning_count', label: '要補充', alignRight: true },
  { id: 'lot_order', label: ['発注', <br />, 'ロット'], alignRight: true },
  { id: 'code_csv', label: ['CSV内', <br />, '商品コード'], alignRight: false },
  { id: '' }
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query, category1, category2, category3) {
  if (query) {
    array = filter(
      array,
      (_target) =>
        _target.code.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        _target.name.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }

  if (category1 !== '-1') {
    array = filter(array, (_target) => _target.category_code1 === category1);
  }
  if (category2 !== '-1') {
    array = filter(array, (_target) => _target.category_code2 === category2);
  }
  if (category3 !== '-1') {
    array = filter(array, (_target) => _target.category_code3 === category3);
  }

  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function PartnerProduct({
  open = false,
  partnerId = 0,
  partnerCode = 0,
  partnerName = '',
  onClose,
  updateProductDt
}) {
  const { SystemLib } = window;
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [modal, setModal] = useState(false);
  const [items, setItems] = useState([]);
  const [currentTargetId, setCurrentTargetId] = useState(0);
  const [updateDt, setUpdateDt] = useState(moment().unix());

  const [isLoading, setIsLoading] = useState(false);
  const [conf, setConf] = useState(false);
  const [confDelAll, setConfDelAll] = useState(false);

  const [category1, setCategory1] = useState('-1');
  const [category2, setCategory2] = useState('-1');
  const [category3, setCategory3] = useState('-1');

  useEffect(() => {
    getItems();
  }, []);

  useEffect(() => {
    getItems();
  }, [partnerCode, updateProductDt]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredItems.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - items.length) : 0;

  const filteredItems = applySortFilter(
    items,
    getComparator(order, orderBy),
    filterName,
    category1,
    category2,
    category3
  );

  const isItemNotFound = filteredItems.length === 0;

  const useStyles = makeStyles({
    tableCell: {
      height: 10
    }
  });

  const getItems = async () => {
    setIsLoading(true);
    const { data } = await SystemLib.api(
      'partner/product/list',
      {
        partner_code: partnerCode
      },
      'post'
    );
    if (data.result) {
      setItems(data.data);
    }
    setIsLoading(false);
  };

  const getItem = async (id) => {
    const { data } = await SystemLib.api(`partner/product/${id}`, {}, 'get');
    if (data.result) {
      let flag = false;
      if (items) {
        const arr = items.slice();
        arr.forEach((v, i) => {
          if (v.id === id) {
            arr[i] = data.data;
            setItems(arr);
            flag = true;
          }
        });
      }
      if (!flag) {
        setOrderBy(null);
        const arr = items.slice();
        arr.unshift(data.data);
        setItems(arr);
      }
    }
  };

  const deleteItems = async (ids) => {
    const tmp = chunk(ids, 1000);
    const p = [];
    tmp.forEach(async (arr) => {
      console.log(arr);
      p.push(
        SystemLib.api(
          `partner/product/delete`,
          {
            ids: arr
          },
          'post'
        )
      );
    });
    await Promise.all(p);
    const newItems = items.filter((v) => !ids.includes(v.id));
    setItems(newItems);
  };

  const reload = async (id) => {
    getItem(id);
  };

  const classes = useStyles();

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        background: '#fff',
        height: '100%',
        width: '100%',
        display: open ? 'block' : 'none'
      }}
    >
      <Page title="パートナー：商品管理">
        <Container sx={{ position: 'relative' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h4" gutterBottom display="block">
              {partnerName}：商品管理
            </Typography>
            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<Icon icon={backFill} />}
                onClick={() => {
                  onClose(partnerId);
                }}
              >
                戻る
              </Button>
              <Button
                variant="contained"
                startIcon={<Icon icon={plusFill} />}
                onClick={() => {
                  setCurrentTargetId(0);
                  setUpdateDt(moment().unix());
                  setModal(true);
                }}
              >
                新規登録
              </Button>
            </Stack>
          </Stack>

          <Card>
            <ListToolbar
              numSelected={selected.length}
              filterName={filterName}
              onFilterName={handleFilterByName}
              onDelete={() => {
                setConfDelAll(true);
              }}
              onChange={(vals) => {
                setCategory1(vals.category1);
                setCategory2(vals.category2);
                setCategory3(vals.category3);
              }}
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <ListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={filteredItems.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                  />
                  <TableBody>
                    {filteredItems
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => {
                        const {
                          id,
                          code,
                          name,
                          price,
                          partner_code,
                          throughput,
                          expiring,
                          // size,
                          // caution_count,
                          // warning_count,
                          category1,
                          category2,
                          category3,
                          lot_order,
                          commission_code,
                          commission,
                          code_csv
                        } = row;
                        const isItemSelected = selected.indexOf(id) !== -1;

                        let category_label = '';
                        if (category1) {
                          category_label = category1.name;
                        }
                        if (category2) {
                          category_label += ` / ${category2.name}`;
                        }
                        if (category3) {
                          category_label += ` / ${category3.name}`;
                        }

                        return (
                          <TableRow
                            hover
                            key={id}
                            tabIndex={-1}
                            role="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isItemSelected}
                                onChange={(event) => handleClick(event, id)}
                              />
                            </TableCell>
                            <TableCell component="th" scope="row" padding="none" width={200}>
                              <Typography variant="subtitle2" noWrap>
                                {code}
                              </Typography>
                            </TableCell>
                            <TableCell component="th" scope="row" padding="normal" width={300}>
                              {category_label}
                            </TableCell>
                            <TableCell
                              component="th"
                              scope="row"
                              padding="none"
                              sx={{ minWidth: 150 }}
                            >
                              <Typography variant="subtitle2" noWrap>
                                {name}
                              </Typography>
                            </TableCell>
                            <TableCell
                              scope="row"
                              padding="none"
                              align="right"
                              sx={{ width: 150, paddingRight: 1 }}
                            >
                              <Typography variant="body2" paddingX={2}>
                                {new Intl.NumberFormat('ja-JP').format(price)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" padding="none" sx={{ width: 150 }}>
                              <Typography variant="body2" paddingX={2}>
                                {commission_code}
                              </Typography>
                            </TableCell>
                            {/* <TableCell align="right" padding="none" sx={{ width: 150 }}>
                              <Typography variant="body2" paddingX={2}>
                                {size}
                              </Typography>
                            </TableCell> */}
                            <TableCell align="right" padding="none" sx={{ width: 150 }}>
                              <Typography variant="body2" paddingX={2}>
                                {throughput}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" padding="none" sx={{ width: 150 }}>
                              <Typography variant="body2" paddingX={2}>
                                {expiring}
                              </Typography>
                            </TableCell>
                            {/* <TableCell align="right" padding="none" sx={{ width: 150 }}>
                              <Typography variant="body2" paddingX={2}>
                                {caution_count}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" padding="none" sx={{ width: 150 }}>
                              <Typography variant="body2" paddingX={2}>
                                {warning_count}
                              </Typography>
                            </TableCell> */}
                            <TableCell align="right" padding="none" sx={{ width: 150 }}>
                              <Typography variant="body2" paddingX={2}>
                                {lot_order}
                              </Typography>
                            </TableCell>
                            <TableCell padding="none" sx={{ width: 200 }}>
                              <Typography variant="body2" paddingX={2}>
                                {code_csv}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" padding="none" sx={{ width: 20 }}>
                              <MoreMenu
                                itemId={id}
                                onEdit={(id) => {
                                  setCurrentTargetId(id);
                                  setUpdateDt(moment().unix());
                                  setModal(true);
                                }}
                                onDelete={(id) => {
                                  setCurrentTargetId(id);
                                  setConf(true);
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={13} />
                      </TableRow>
                    )}
                  </TableBody>
                  {isItemNotFound && !isLoading && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={13} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filterName} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                  {isItemNotFound && isLoading && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={13} sx={{ py: 3 }}>
                          <Paper>
                            <Typography gutterBottom align="center" variant="subtitle1">
                              読み込み中
                            </Typography>
                          </Paper>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 50, 100]}
              component="div"
              count={filteredItems.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="表示件数"
            />
          </Card>
        </Container>
        {modal && (
          <PartnerProductModal
            targetGroup={partnerCode}
            targetId={currentTargetId}
            open={modal}
            onClose={() => {
              setModal(false);
            }}
            updateDt={updateDt}
            reload={(id) => {
              reload(id);
            }}
          />
        )}
        <ConfDialog
          open={conf}
          onOk={() => {
            deleteItems([currentTargetId]);
            setCurrentTargetId(0);
            setConf(false);
          }}
          onCancel={() => {
            setConf(false);
          }}
          onClose={() => {
            setConf(false);
          }}
        >
          削除してもよろしいでしょうか？
        </ConfDialog>
        <ConfDialog
          open={confDelAll}
          onOk={() => {
            deleteItems(selected);
            setSelected([]);
            setConfDelAll(false);
          }}
          onCancel={() => {
            setConfDelAll(false);
          }}
          onClose={() => {
            setConfDelAll(false);
          }}
        >
          削除してもよろしいでしょうか？ ({selected.length}件)
        </ConfDialog>
      </Page>
    </div>
  );
}
